import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  IconButton,
  Skeleton,
  Stack,
  TablePagination,
  styled,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { formatNumberWithCommas } from "../../../../Utilities/FormatNumbers";
import XLSX from "xlsx";
import { utils, writeFile } from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import { canExportToCSV } from "../config";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";

const VolumeAnalysisTable = () => {
  const volumeData = useSelector((state) => state.VolumeAnalysis);
  const authToken = useSelector((state) => state.authToken);
  const isLoading = useSelector((state) => state.isLoading);
  const rowsPerPage = useSelector((state) => state.RowsPerPage); // Redux state
  const page = useSelector((state) => state.SetPage); // Redux state
  const searchItem = useSelector((state) => state.SearchItems); // Redux state
  const dispatch = useDispatch();
  const ASCENDING = "ascending";
  const DESCENDING = "descending";
  const [ArrowIcon, setArrowIcon] = useState(true);

  useEffect(() => {
    const fetchIndicatorData = async () => {
      try {
        dispatch({ type: "IS_LOADING", payload: true });

        const IndicatorApi =
          "https://idartsuat.com/api/eodanalytics/BulkData/VolumeAnalysis";
        const response = await axios.get(IndicatorApi, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (response.status === 200) {
          dispatch({
            type: "SET_VOLUME_ANALYSIS",
            payload: response.data?.Data,
          });
        }
        dispatch({ type: "IS_LOADING", payload: false });
      } catch (error) {
        console.error("Error fetching data:", error);
        dispatch({ type: "IS_LOADING", payload: false });
      }
    };

    fetchIndicatorData();
  }, [authToken, dispatch]);

  // const formatNumber = (num) => {
  //   if (num >= 1000000) {
  //     return (num / 1000000).toFixed(1) + 'M';
  //   } else if (num >= 100000) {
  //     return (num / 100000).toFixed(1) + 'L';
  //   } else if (num >= 1000) {
  //     return (num / 1000).toFixed(1) + 'K';
  //   }
  //   return num.toString();
  // };

  const LeftAlignTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "2px 4px",
    },
    textAlign: "left",

    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "2px 4px",
    },
    textAlign: "left",
  }));
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "2px 4px",
      textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
      borderRight: `1px solid ${theme.palette.divider}`,
      textAlign: "right",
      padding: "2px 4px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      // backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    height: "36px",
  }));

  const manipulateData = (data) => {
    if (!data) return null;

    let totalData = data?.RootData.slice(2);
    let nseData = Array(6).fill("-");
    let bseData = Array(6).fill("-");

    data?.Values.forEach((element) => {
      if (element.Exchange === "NSE") {
        nseData = element.Details.map((value) => value.toLocaleString());
      } else if (element.Exchange === "BSE") {
        bseData = element.Details.map((value) => value.toLocaleString());
      }
    });

    if (totalData[0] === "0" || totalData[1] === "0" || totalData[3] === "0") {
      return null;
    }
    return {
      totalData: totalData,
      nseData,
      bseData,
      symbol: data?.RootData[1],
    };
  };

  const dataIndex = {
    avg_trade_ltd: 0,
    avg_delivered_ltd: 1,
    avg_trade_ltd_1wk: 2,
    avg_delivered_ltd_1wk: 3,
    avg_trade_ltd_1mo: 4,
    avg_delivered_ltd_1mo: 5,
  };

  const [sortConfig, setSortConfig] = useState({
    key: "avg_trade_ltd",
    direction: DESCENDING,
  });

  const sortedVolData = [...volumeData].sort((a, b) => {
    const index = dataIndex[sortConfig.key] + 2;
    const aData = parseInt(a.RootData[index]);
    const bData = parseInt(b.RootData[index]);
    if (sortConfig.direction === DESCENDING) return bData - aData;
    return aData - bData;
  });

  const rowsPerPageOptions = [5, 10, 25];

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
    dispatch({ type: "SET_PAGE", payload: 0 });
  };

  const handleLastPageButtonClick = (event, count) => {
    handleChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const handleSortClick = (key) => {
    setSortConfig((prev) => ({
      key: key,
      direction:
        prev.key === key && prev.direction === DESCENDING
          ? ASCENDING
          : DESCENDING,
    }));
  };

  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;

    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
        <IconButton
          onClick={(event) => onPageChange(event, 0)}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(event) => handleLastPageButtonClick(event, count)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    );
  };

  const filterData = (data) => {
    if (!data) return [];
    if (!searchItem || searchItem.length === 0) return data;
    return data.filter((item) => {
      if (item == null || item == undefined) return false;
      return item.symbol
        .toLowerCase()
        .includes(searchItem[searchItem.length - 1].toLowerCase());
    });
  };
  const manipulatedData = sortedVolData.map(manipulateData);
  const filteredData = filterData(manipulatedData).filter(
    (data) => data !== null,
  );

  const handleExportCSV = () => {
    if (!volumeData) return;

    const sortedFilteredData = filterData(filteredData);

    // Prepare data for export
    const dataToExport = sortedFilteredData?.map((data) => ({
      SYMBOL: data.symbol,
      EXCHANGE: "NSE\nBSE\nTotal",
      LTD1:
        data?.nseData[0] + "\n" + data?.bseData[0] + "\n" + data?.totalData[0],
      "1WK1":
        data?.nseData[2] + "\n" + data?.bseData[2] + "\n" + data?.totalData[2],
      "1MO1":
        data?.nseData[4] + "\n" + data?.bseData[4] + "\n" + data?.totalData[4],
      LTD2:
        data?.nseData[1] + "\n" + data?.bseData[1] + "\n" + data?.totalData[1],
      "1WK2":
        data?.nseData[3] + "\n" + data?.bseData[3] + "\n" + data?.totalData[3],
      "1MO2":
        data?.nseData[5] + "\n" + data?.bseData[5] + "\n" + data?.totalData[5],
    }));

    if (dataToExport.length === 0) {
      console.warn("No data to export.");
      return;
    }

    // Convert data to worksheet
    const ws = utils.json_to_sheet(dataToExport, {
      header: [
        "SYMBOL",
        "EXCHANGE",
        "LTD1",
        "1WK1",
        "1MO1",
        "LTD2",
        "1WK2",
        "1MO2",
      ],
    });
    ws["!cols"] = [
      { wpx: 200 }, // Width for SYMBOL
      { wpx: 300 }, // Width for EXCHANGE
      { wpx: 150 }, // Width for LTD1
      { wpx: 150 }, // Width for 1WK1
      { wpx: 150 }, // Width for 1MO1
      { wpx: 150 }, // Width for LTD2
      { wpx: 150 }, // Width for 1WK2
      { wpx: 150 }, // Width for 1MO2
    ];

    // Create a new workbook
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "VolumeAnalysis");

    // Export the workbook as a CSV file
    writeFile(wb, "volume_analysis.csv"); // Export as CSV
  };
  return (
    <>
      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
          </div>
        </Stack>
      ) : (
        <div>
          <TableContainer sx={{ maxHeight: 350 }}>
            {canExportToCSV && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  padding: "6px",
                  cursor: "pointer",
                }}
              >
                <DownloadIcon
                  onClick={handleExportCSV}
                  aria-label="export to CSV"
                />
              </div>
            )}
            <Table stickyHeader aria-label="volume analysis table">
              <TableHead>
                <StyledTableRow
                  sx={{ position: "sticky", top: 0, height: "36px" }}
                >
                  <StyledTableCell rowSpan={2}>SYMBOLS</StyledTableCell>
                  <StyledTableCell rowSpan={2}>EXCHANGE</StyledTableCell>
                  <StyledTableCell colSpan={3}>
                    Avg Traded Volume
                  </StyledTableCell>
                  <StyledTableCell colSpan={3}>
                    Avg Delivered Volume
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow
                  sx={{
                    position: "sticky",
                    top: 36,
                    height: "36px",
                    cursor: "pointer",
                  }}
                >
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_trade_ltd");
                      setArrowIcon(!ArrowIcon);
                    }}
                  >
                    LTD
                    {sortConfig.key === "avg_trade_ltd" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_trade_ltd_1wk");
                      setArrowIcon(!ArrowIcon);
                    }}
                  >
                    1WK
                    {sortConfig.key === "avg_trade_ltd_1wk" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_trade_ltd_1mo");
                      setArrowIcon(!ArrowIcon);
                    }}
                    // sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'36px'}}
                  >
                    1MO
                    {sortConfig.key === "avg_trade_ltd_1mo" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_delivered_ltd");
                      setArrowIcon(!ArrowIcon);
                    }}
                  >
                    LTD
                    {sortConfig.key === "avg_delivered_ltd" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_delivered_ltd_1wk");
                      setArrowIcon(!ArrowIcon);
                    }}
                  >
                    1WK
                    {sortConfig.key === "avg_delivered_ltd_1wk" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      handleSortClick("avg_delivered_ltd_1mo");
                      setArrowIcon(!ArrowIcon);
                    }}
                    // sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'36px'}}
                  >
                    1MO
                    {sortConfig.key === "avg_delivered_ltd_1mo" ? (
                      ArrowIcon ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )
                    ) : (
                      <SwapVertIcon sx={{ height: "18px" }} />
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={7} align="center">
                      NO DATA FOUND
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((data, index) => (
                      <StyledTableRow key={index}>
                        <LeftAlignTableCell>{data.symbol}</LeftAlignTableCell>
                        <LeftAlignTableCell>
                          <p style={{ margin: 0 }}>NSE</p>
                          <p style={{ margin: 0 }}>BSE</p>
                          <p style={{ margin: 0, fontWeight: "bolder" }}>
                            Total
                          </p>
                        </LeftAlignTableCell>
                        {Array.from([0, 2, 4, 1, 3, 5], (key, _) => (
                          <StyledTableCell key={key}>
                            <p style={{ margin: 0 }}>
                              {formatNumberWithCommas(data?.nseData[key])}
                            </p>
                            <p style={{ margin: 0 }}>
                              {formatNumberWithCommas(data?.bseData[key])}
                            </p>
                            <p style={{ margin: 0, fontWeight: "bolder" }}>
                              {formatNumberWithCommas(data?.totalData[key])}
                            </p>
                          </StyledTableCell>
                        ))}
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </div>
      )}
    </>
  );
};

export default VolumeAnalysisTable;
